import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import AceEditor from "react-ace";

import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { RichTreeView, TreeViewBaseItem } from '@mui/x-tree-view';

import { Editor } from 'ace-builds';
import "ace-builds/src-noconflict/ext-language_tools";
// import "ace-builds/src-noconflict/mode-java";
import 'ace-builds/src-noconflict/mode-sql';
// import "ace-builds/src-noconflict/theme-github";
import 'ace-builds/src-noconflict/theme-sqlserver';

import DataTable from '../../../components/tables/DataTable';
import { Column } from '../../../components/tables/interfaces';
import { Connection, Schema, StatementColumn } from '../../../utils/models/sqlpad';


interface SqlEditorProps {
    value: string;
    connectionSchema: Schema[];
    connectionsList: Connection[];
    selectedConnectionId: string;
    queryResultColumns: StatementColumn[];
    queryResultData: any[][];
    errorInQueryResult: string;
    isQueryResultsLoading: boolean;
    hasQueryRun: boolean;
    errorInConnectionSchema: string;
    setSelectedConnectionId: Dispatch<SetStateAction<string>>;
    setErrorInConnectionSchema: Dispatch<SetStateAction<string>>;
    onChange: (value: string) => void;
    fetchDbResultOnRunQuery: () => void;
}

const TABLE_TOOLBAR_HEIGHT = 40

const modifyConnectionsSchemaForTree = (conn_schema: Schema[]): TreeViewBaseItem[] => {
    return conn_schema.map((schema, schemaIndex) => ({
        id: `schema-${schemaIndex}`,
        label: schema.name,
        children: schema.tables.map((table, tableIndex) => ({
            id: `schema-${schemaIndex}-table-${tableIndex}`,
            label: table.name,
            children: table.columns.map((col, colIndex) => ({
                id: `schema-${schemaIndex}-table-${tableIndex}-column-${colIndex}`,
                label: `${col.name} (${col.dataType})`
            }))
        }))
    }))
}

const modifyTableColumns = (columns: StatementColumn[]): Column[] => {
    return columns.map((col) => ({
        id: col.name,
        label: col.name,
        ...(col?.datatype && col?.datatype === 'object' && {
            render: (value: any) => {
                return JSON.stringify(value);
            }
        })
    }))
}

const getSchemaAutocomplete = (conn_schema: Schema[]) => {
    let completions: { caption: string; value: string; meta: string }[] = [];

    conn_schema.forEach((schema) => {
        completions.push({ caption: schema.name, value: schema.name, meta: 'schema' });

        schema.tables.forEach((table) => {
            completions.push({ caption: table.name, value: table.name, meta: 'table' });

            table.columns.forEach((column) => {
                completions.push({ caption: column.name, value: column.name, meta: 'column' });
            });
        });
    });

    return completions;
};

const SqlEditor: React.FC<SqlEditorProps> = ({
    value,
    connectionSchema,
    connectionsList,
    selectedConnectionId,
    queryResultColumns,
    queryResultData,
    errorInQueryResult,
    isQueryResultsLoading,
    hasQueryRun,
    errorInConnectionSchema,
    setErrorInConnectionSchema,
    setSelectedConnectionId,
    onChange,
    fetchDbResultOnRunQuery,
}) => {

    const tableBoxRef = useRef<HTMLDivElement>(null);

    const connectionSchemaTree: TreeViewBaseItem[] = modifyConnectionsSchemaForTree(connectionSchema);
    const tableColumns: Column[] = modifyTableColumns(queryResultColumns);
    const [editor, setEditor] = useState<Editor | null>(null);

    const fetchData = async () => {
        return queryResultData;
    }

    const dataExtractor = (response: any[][]) => {
        return response.map(item => {
            return tableColumns.reduce((acc, col, index) => {
                acc[col.id] = item[index] === null ? "null" : String(item[index]);
                return acc;
            }, {} as Record<string, any>)
        })
    }

    useEffect(() => {
        if (editor) {
            const customCompleter = {
                getCompletions: (editor: any, session: any, pos: any, prefix: string, callback: Function) => {
                    callback(null, getSchemaAutocomplete(connectionSchema));
                },
            };

            editor.completers = [...(editor.completers || []), customCompleter];

            editor.commands.on('afterExec', (e: any) => {
                if (e.command.name === 'insertstring' && /^[\w.]$/.test(e.args)) {
                    if (e.args === '.') {
                        editor.execCommand('startAutocomplete');
                    }
                }
            });
        }
    }, [connectionSchema, editor]);

    return (
        <Box>
            {/* header */}
            <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                sx={{ borderBottom: 1, borderBottomColor: 'divider', px: 2 }}
            >
                <Box width={224} sx={{ borderRight: 1, borderRightColor: 'divider' }}>
                    <FormControl size='small' fullWidth>
                        <InputLabel
                            id="simple-select-label-connection"
                            shrink={false}
                            sx={{
                                display: selectedConnectionId ? "none" : "block",
                                transition: "opacity 0.2s",
                                opacity: 1,
                                "&.Mui-focused": {
                                    opacity: 0,
                                },
                            }}
                        >
                            Select Connection
                        </InputLabel>
                        <Select
                            fullWidth
                            labelId="simple-select-label-connection"
                            label="Select Connection"
                            placeholder='Select Connection'
                            value={selectedConnectionId}
                            onChange={e => {
                                setSelectedConnectionId(e.target.value)
                                setErrorInConnectionSchema('')
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        '& .MuiMenuItem-root': { py: 1 },
                                    },
                                },
                                MenuListProps: {
                                    sx: { pt: 0, pb: 0, },
                                },
                            }}
                            sx={{
                                '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                            }}
                        >
                            {connectionsList.length > 0 ?
                                connectionsList.map(item => (
                                    <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                ))
                                :
                                <MenuItem disabled>no connections available</MenuItem>
                            }
                        </Select>
                    </FormControl>
                </Box>

                <Box>
                    <Button
                        variant='contained'
                        size='small'
                        onClick={fetchDbResultOnRunQuery}
                        disabled={!selectedConnectionId || !!errorInConnectionSchema}
                    >
                        run
                    </Button>
                </Box>
            </Box>

            {/* body */}
            <Box display='flex' height="calc(100vh - 141px)">
                {/* left panel */}
                <Box sx={{ width: 240, minWidth: 240, p: 1, borderRight: '1px solid', borderRightColor: 'divider', overflow: 'auto' }}>
                    {/* schema tree structure */}
                    {!errorInConnectionSchema ?
                        <RichTreeView
                            disableSelection
                            items={connectionSchemaTree}
                            sx={{
                                "& .MuiTreeItem-label": { fontSize: "14px !important" }
                            }}
                        />
                        :
                        <Box sx={{ color: 'red', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            {errorInConnectionSchema}
                        </Box>
                    }
                </Box>

                {/* right body panel */}
                <Box sx={{
                    width: '100%',
                    height: '100%',
                }}>
                    {/* editor */}
                    <Box sx={{ width: '100%', height: '40%', overflow: 'auto' }}>
                        <AceEditor
                            editorProps={{ $blockScrolling: Infinity }}
                            mode="sql"
                            theme="sqlserver"
                            fontSize={14}
                            value={value}
                            onLoad={(editor) => setEditor(editor)}
                            onChange={onChange}
                            name="sql_ace_editor"
                            highlightActiveLine={false}
                            width={'100%'}
                            height='100%'
                            setOptions={{
                                useWorker: true,
                                enableBasicAutocompletion: true,
                                enableLiveAutocompletion: true,
                                enableSnippets: false,
                                showLineNumbers: true,
                                tabSize: 2,
                            }}
                            showPrintMargin={false}
                        />

                        {/* <JSONEditor
                            language="sql"
                            schemaValue={"{}"}
                            isSchemaSampleDataOn={false}
                            value={''}
                            onChange={(value: string | undefined) => {
                                // setFileStates(prevData => ({ ...prevData, [selectedFile]: value || "" }))
                            }}
                        /> */}
                    </Box>

                    {/* table */}
                    <Box
                        ref={tableBoxRef}
                        sx={{ width: '100%', height: '60%', borderTop: 1, borderTopColor: 'divider', overflow: 'auto' }}
                    >
                        <Box sx={{
                            py: 0.5,
                            px: 1,
                            height: `${TABLE_TOOLBAR_HEIGHT}px`,
                            borderBottom: 1,
                            borderBottomColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'end',
                            backgroundColor: '#ebebeb',
                        }}
                        >
                            <Typography variant='body2'>{queryResultData.length} rows</Typography>
                        </Box>

                        <Box height={`calc(100% - ${TABLE_TOOLBAR_HEIGHT}px)`}>
                            {queryResultData && !errorInQueryResult ?
                                <>
                                    {isQueryResultsLoading ?
                                        <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' height='100%' gap={2}>
                                            <CircularProgress size={30} />
                                            <Typography variant='body2' component='p'>Fetching results...</Typography>
                                        </Box>
                                        :
                                        <>
                                            {queryResultData?.length > 0 ? (
                                                <DataTable
                                                    columns={tableColumns}
                                                    fetchData={fetchData}
                                                    dataExtractor={dataExtractor}
                                                    size='small'
                                                    tableContainerMaxHeight={tableBoxRef.current ? `${tableBoxRef.current?.getBoundingClientRect().height - (TABLE_TOOLBAR_HEIGHT + 2)}px` : ''}
                                                    stickyHeader
                                                    hidePagination
                                                />
                                            ) : (
                                                hasQueryRun && (
                                                    <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' height='100%'>
                                                        <Typography variant='h6' component='p' color='gray'>Query finished</Typography>
                                                        <Typography variant='h6' component='p' color='gray'>No rows returned</Typography>
                                                    </Box>
                                                )
                                            )}
                                        </>
                                    }
                                </>
                                :
                                <Box p={2} sx={{ color: 'red', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    {errorInQueryResult}
                                </Box>
                            }
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default SqlEditor